import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, FileText, Award, Target, Users } from 'lucide-react';
import { PersonalTab, DocumentsTab, FamilyTab, TravelTab, LeadTab } from '@/components/AddTraveller';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditTraveller = () => {
  const { ucc } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // BATCH 1 — GET /clients/{ucc} returns PII masked/stripped. We load the
  // sensitive fields BLANK (never the mask) so a blind Save can't clobber the
  // real encrypted value; presence flags drive a "leave blank to keep" hint.
  const [piiOnFile, setPiiOnFile] = useState({ passportNumber: false, panCard: false, aadharCard: false });
  
  const [formData, setFormData] = useState({
    fullName: '',
    givenName: '',
    surname: '',
    companyName: '',
    companyGstNumber: '',
    companyAddress: '',
    email: '',
    phone: '',
    countryCode: '+91',
    gender: '',
    nationality: '',
    maritalStatus: '',
    numberOfKids: 0,
    dateOfBirth: '',
    marriageAnniversary: '',
    address: '',
    pincode: '',
    passportNumber: '',
    passportIssueDate: '',
    passportExpiryDate: '',
    passportIssuedAt: '',
    panCard: '',
    aadharCard: '',
    travelCategory: 'budget',
    leadSource: '',
    leadGeneratedDate: '',
    referredBy: '',
    familyMembers: []
  });

  useEffect(() => {
    let active = true;
    const fetchTraveller = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/clients/${ucc}`);
      if (!active) return;
      const data = response.data;
      setFormData({
        fullName: data.fullName || '',
        // Phase 16.2.1 — back-fill split-name fields. Legacy rows only have
        // `fullName`, so derive given/surname from a last-whitespace split
        // (matches backend's `build_traveller_doc` heuristic).
        givenName: data.givenName || ((data.fullName || '').trim().split(/\s+/).slice(0, -1).join(' ') || (data.fullName || '').trim()),
        surname: data.surname || (((data.fullName || '').trim().split(/\s+/).length > 1) ? (data.fullName || '').trim().split(/\s+/).slice(-1).join('') : ''),
        // Phase 17.2 #8 — corporate identity fields.
        companyName: data.companyName || '',
        companyGstNumber: data.companyGstNumber || '',
        companyAddress: data.companyAddress || '',
        email: data.email || '',
        phone: data.phone || '',
        countryCode: data.countryCode || '+91',
        gender: data.gender || '',
        nationality: data.nationality || '',
        maritalStatus: data.maritalStatus || '',
        numberOfKids: data.numberOfKids || 0,
        dateOfBirth: data.dateOfBirth || '',
        marriageAnniversary: data.marriageAnniversary || '',
        address: data.address || '',
        pincode: data.pincode || '',
        // BATCH 1 — never load the masked value into an editable field.
        passportNumber: '',
        passportIssueDate: data.passportIssueDate || '',
        passportExpiryDate: data.passportExpiryDate || '',
        passportIssuedAt: data.passportIssuedAt || '',
        panCard: '',
        aadharCard: '',
        travelCategory: data.travelCategory || 'budget',
        leadSource: data.leadSource || '',
        leadGeneratedDate: data.leadGeneratedDate || '',
        referredBy: data.referredBy || '',
        familyMembers: data.familyMembers || []
      });
      setPiiOnFile({
        passportNumber: !!data.hasPassport,
        panCard: !!data.hasPan,
        aadharCard: !!data.hasAadhaar,
      });
    } catch (error) {
      toast.error('Failed to load traveller');
      navigate('/clients');
    } finally {
      if (active) setLoading(false);
    }
  };
    fetchTraveller();
    return () => { active = false; };
  }, [ucc, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(`${BACKEND_URL}/api/clients/${ucc}`, formData);
      toast.success('Traveller updated successfully!');
      navigate(`/clients/${ucc}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update traveller');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={() => navigate(`/clients/${ucc}`)} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Traveller
        </Button>
      </div>

      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/5 to-accent-600/5 rounded-3xl blur-3xl"></div>
        <div className="relative">
          <h1 className="text-4xl lg:text-5xl font-heading font-bold text-slate-900 tracking-tight mb-3">
            Edit Traveller
          </h1>
          <p className="text-base lg:text-lg text-slate-600 font-light">
            <span className="font-mono text-primary-700">{ucc}</span> - {formData.fullName}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="personal" className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-10 h-14 bg-white/80 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-1.5 shadow-sm">
            <TabsTrigger value="personal" data-testid="tab-personal" className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all text-xs sm:text-sm">
              <User className="h-4 w-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Personal</span>
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents" className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all text-xs sm:text-sm">
              <FileText className="h-4 w-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Documents</span>
            </TabsTrigger>
            <TabsTrigger value="family" data-testid="tab-family" className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all text-xs sm:text-sm">
              <Users className="h-4 w-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Family</span>
            </TabsTrigger>
            <TabsTrigger value="travel" data-testid="tab-travel" className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all text-xs sm:text-sm">
              <Award className="h-4 w-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Travel</span>
            </TabsTrigger>
            <TabsTrigger value="lead" data-testid="tab-lead" className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all text-xs sm:text-sm">
              <Target className="h-4 w-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Lead</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="personal" className="animate-fade-in">
            <PersonalTab formData={formData} handleChange={handleChange} handleSelectChange={handleSelectChange} />
          </TabsContent>

          <TabsContent value="documents" className="animate-fade-in">
            <DocumentsTab formData={formData} handleChange={handleChange} handleSelectChange={handleSelectChange} ucc={ucc} piiOnFile={piiOnFile} />
          </TabsContent>

          <TabsContent value="family" className="animate-fade-in">
            <FamilyTab formData={formData} setFormData={setFormData} ucc={ucc} />
          </TabsContent>

          <TabsContent value="travel" className="animate-fade-in">
            <TravelTab formData={formData} handleSelectChange={handleSelectChange} />
          </TabsContent>

          <TabsContent value="lead" className="animate-fade-in">
            <LeadTab formData={formData} handleChange={handleChange} handleSelectChange={handleSelectChange} />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end space-x-4 mt-10">
          <Button type="button" variant="outline" onClick={() => navigate(`/clients/${ucc}`)} data-testid="cancel-btn" className="px-8 py-3 h-12 rounded-xl">Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="save-traveller-btn" className="bg-gradient-to-br from-primary-700 to-primary-600 text-white shadow-lg px-10 py-3 h-12 rounded-xl font-semibold">
            <Save className="h-5 w-5 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditTraveller;
